// src/components/layout/Footer.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getCategoriesWithImages } from '../../services/api';

const API = 'https://madadline.com/api';

function Footer() {
  const { t } = useTranslation();

  const [phone, setPhone] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null); // 'success' | 'error' | null
  const [subscribeMessage, setSubscribeMessage] = useState('');
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [categories, setCategories] = useState([]);

  /* =========================================================
     CATÉGORIES
  ========================================================= */
  useEffect(() => {
    getCategoriesWithImages()
      .then(cats => {
        const list = Array.isArray(cats) ? cats : [];
        const formatted = list
          .filter(cat => cat && (cat.name || cat.categorie))
          .map((cat, i) => {
            const name = cat.name || cat.categorie || '';
            const slug = cat.slug
              || name.toLowerCase().normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
            return { label: name, slug: slug || `cat-${i}` };
          })
          .filter(c => c.label);
        setCategories(formatted);
      })
      .catch(() => setCategories([]));
  }, []);

  /* =========================================================
     BOUTON RETOUR EN HAUT
  ========================================================= */
  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* =========================================================
     NEWSLETTER — soumission réelle
  ========================================================= */
  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribeStatus(null);
    setSubscribeMessage('');

    if (!phone.trim()) {
      setSubscribeStatus('error');
      setSubscribeMessage(t('footer.subscribeEmptyPhone') || 'Veuillez entrer votre numéro.');
      return;
    }

    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        phone: phone.trim(),
        consent: true,
      });

      setSubscribeStatus('success');
      setSubscribeMessage(
        data?.message || t('footer.subscribeSuccess') || 'Merci ! Vous êtes bien inscrite.'
      );
      setPhone('');

      // Masquer le message après 6 secondes
      setTimeout(() => {
        setSubscribeStatus(null);
        setSubscribeMessage('');
      }, 6000);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.errors?.phone?.[0] ||
        t('footer.subscribeError') ||
        'Erreur lors de l’inscription. Merci de réessayer.';
      setSubscribeStatus('error');
      setSubscribeMessage(msg);
    } finally {
      setSubscribing(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const year = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footer__inner">

        {/* ============ COLONNE 1 — MARQUE ============ */}
        <div className="footer__col footer__col--brand">
          <Link to="/" className="footer__logo">
            <svg viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg" aria-label="MADAD" className="footer__logo-svg">
              <text x="100" y="26" textAnchor="middle" fontFamily="'Tajawal', sans-serif" fontSize="24" fontWeight="700" fill="#F5F3E1">مداد</text>
              <text x="100" y="50" textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontSize="22" fontWeight="700" letterSpacing="6" fill="#F5F3E1">MADAD</text>
              <line x1="60" y1="56" x2="140" y2="56" stroke="#C21E56" strokeWidth="1" opacity="0.6" />
            </svg>
          </Link>

          <p className="footer__about">{t('footer.about')}</p>
          <div className="footer__socials">
            <a href="https://instagram.com/madadline" className="footer__social" target="_blank" rel="noreferrer" aria-label="Instagram">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
              </svg>
            </a>
            <a href="https://wa.me/212669428878" className="footer__social" target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              </svg>
            </a>
            <a href="mailto:madadline@gmail.com" className="footer__social" aria-label="Email">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m2 6 10 7 10-7" />
              </svg>
            </a>
          </div>
        </div>

        {/* ============ COLONNE 2 — BOUTIQUE ============ */}
        <div className="footer__col">
          <h4 className="footer__title">{t('footer.shop')}</h4>
          <ul className="footer__list">
            <li><Link to="/nouveautes">{t('footer.newArrivals')}</Link></li>
            {categories.length > 0 ? (
              categories.map(cat => (
                <li key={cat.slug}><Link to={`/${cat.slug}`}>{cat.label}</Link></li>
              ))
            ) : (
              <li className="footer__list-loading">{t('footer.loading')}</li>
            )}
          </ul>
        </div>

        {/* ============ COLONNE 3 — AIDE ============ */}
        <div className="footer__col">
          <h4 className="footer__title">{t('footer.help')}</h4>
          <ul className="footer__list">
            <li><Link to="/notre-histoire">{t('footer.ourStory')}</Link></li>
            <li><Link to="/guide-des-tailles">{t('footer.sizeGuide')}</Link></li>
            <li><Link to="/livraison-echanges">{t('footer.shippingReturns')}</Link></li>
            <li><Link to="/faq">{t('footer.faq')}</Link></li>
            <li><Link to="/suivi-commande">{t('footer.orderTracking')}</Link></li>
            <li><Link to="/contact">{t('footer.contact')}</Link></li>
          </ul>
        </div>

        {/* ============ COLONNE 4 — CONTACT + NEWSLETTER ============ */}
        <div className="footer__col footer__col--contact">
          <h4 className="footer__title">{t('footer.contactTitle')}</h4>
          <ul className="footer__contact">
            <li>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {t('footer.address')}
            </li>
            <li>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <a href="tel:+212669428878">+212669428878</a>
            </li>
            <li>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m2 6 10 7 10-7" />
              </svg>
              <a href="mailto:madadline@gmail.com">madadline@gmail.com</a>
            </li>
            <li>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {t('footer.hours')}
            </li>
          </ul>

          <h4 className="footer__title footer__title--news">{t('footer.newsletterTitle')}</h4>
          <p className="footer__news-text">{t('footer.newsletterText')}</p>

          <form onSubmit={handleSubscribe} className="footer__news-form">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('footer.phonePlaceholder')}
              required
              aria-label={t('footer.phoneAria')}
              disabled={subscribing}
            />
            <button
              type="submit"
              aria-label={t('footer.subscribeAria')}
              disabled={subscribing}
            >
              {subscribing ? (
                <svg
                  width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2"
                  style={{ animation: 'footerSpin 0.8s linear infinite' }}
                >
                  <circle cx="12" cy="12" r="10" opacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </form>

          {subscribeStatus === 'success' && (
            <div className="footer__news-success">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {subscribeMessage}
            </div>
          )}

          {subscribeStatus === 'error' && (
            <div className="footer__news-error">
              {subscribeMessage}
            </div>
          )}

          <div className="footer__payment-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <circle cx="12" cy="12" r="2.5" />
            </svg>
            {t('footer.cashOnDelivery')}
          </div>
        </div>
      </div>

      {/* ============ BAS ============ */}
      <div className="footer__bottom">
        <div className="footer__bottom-inner">
          <span className="footer__copyright">{t('footer.copyright', { year })}</span>
          <div className="footer__bottom-links">
            <Link to="/mentions-legales">{t('footer.legal')}</Link>
            <span className="footer__sep">·</span>
            <Link to="/cgv">{t('footer.terms')}</Link>
            <span className="footer__sep">·</span>
            <Link to="/confidentialite">{t('footer.privacy')}</Link>
          </div>
          <span className="footer__payment">{t('footer.paymentNote')}</span>
        </div>
      </div>

      {/* ============ RETOUR EN HAUT ============ */}
      {showTopBtn && (
        <button
          className="footer__back-top"
          onClick={scrollToTop}
          aria-label={t('footer.backToTop')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
      )}

      {/* ⭐ Animation spinner (si pas déjà dans ton CSS global) */}
      <style>{`
        @keyframes footerSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </footer>
  );
}

export default Footer;